import { useState, useEffect } from 'react';
import { useDynamicEntity } from './useDynamicEntity';
import { useAuth } from './index';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_UNASSIGNED_JOBS,
  GET_UNASSIGNED_JOBS_FOR_CREATOR,
} from '../graphql/queries';
import { useTranslation } from 'react-i18next';

// Unrestricted: ROOT/DIRECTOR/GROUP_MANAGER tüm şirket atanmamış işlerini
// görür. Diğer roller (TEAM_MEMBER/TEAM_MANAGER) sadece kendi yarattıkları
// işleri görür — kullanıcı oluşturduğu işi en azından burada görüp duplicate
// açmasın diye. Bkz: GET_UNASSIGNED_JOBS_FOR_CREATOR yorumu.
const UNRESTRICTED_UNASSIGNED_ROLES = new Set([
  'ROOT', 'DIRECTOR', 'GROUP_MANAGER',
]);

const useJobAssignments = ({ paginated = false } = {}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [unassignedJobs, setUnassignedJobs] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(true);
  
  const dynamicHook = useDynamicEntity({
    entityType: 'jobAssignments',
    gqlEntityKey: 'job_assignments',
    queryName: 'GET_JOB_ASSIGNMENTS',
    createMutation: 'CREATE_JOB_ASSIGNMENT',
    updateMutation: 'UPDATE_JOB_ASSIGNMENT',
    deleteMutation: 'DELETE_JOB_ASSIGNMENT',
    updateVariableName: 'set',
    serverPagination: paginated,
    aggregateKey: paginated ? 'job_assignments_aggregate' : null,
    transformData: (assignment) => {
      const job = assignment.job || {};
      const technicalUser = assignment.technical_user;
      const financialUser = assignment.financial_user;

      const warnings = [];
      let warningType = 'NONE';

      const today = new Date();
      const techLeft =
        !!technicalUser?.work_end_date &&
        new Date(technicalUser.work_end_date) <= today;

      if (techLeft) {
        warningType = 'TECH_LEFT';
        const name = `${technicalUser.first_name || ''} ${technicalUser.last_name || ''}`.trim();
        warnings.push(
          t('jobAssignments.warnings.technicalUserLeft', {
            name: name || t('common.unknownUser', 'Bilinmeyen kullanıcı'),
          })
        );
      }

      const scope = job.consulting_scope || 'MALI_TEKNIK';
      // İş atamasında "Mali sorumlu istenmiyor" işaretli ise (jobs.no_financial_needed=true)
      // uyarı bastırılır — kullanıcı bilinçli olarak mali atamasını kapattı.
      const needsFinancial =
        job.job_status === 'ONAYLANDI' &&
        !financialUser &&
        scope !== 'SADECE_TEKNIK' &&
        !job.no_financial_needed;

      if (needsFinancial) {
        warningType =
          warningType === 'NONE' ? 'NO_FINANCIAL' : 'TECH_LEFT_AND_NO_FINANCIAL';
        warnings.push(t('jobAssignments.warnings.missingFinancial'));
      }

      return {
        ...assignment,
        jobTitle: job.job_title || '-',
        projectNumber: job.project_number || '-',
        consultingScope: job.consulting_scope || 'MALI_TEKNIK',
        jobStatus: job.job_status || '-',
        jobType: job.job_type_obj?.name || job.job_type || '-',
        createdByUser: job.user
          ? `${job.user.first_name || ''} ${job.user.last_name || ''}`.trim() || '-'
          : '-',
        companyName: assignment.company?.name || '-',
        firmName: job.firm?.firm_name || '-',
        firmShortName: job.firm?.firm_short_name || '-',
        job,

        technicalTeamName: assignment.technical_team?.name || '-',
        technicalUserName: technicalUser
          ? `${technicalUser.first_name} ${technicalUser.last_name}`
          : '-',

        financialTeamName: assignment.financial_team?.name || '-',
        financialUserName: financialUser
          ? `${financialUser.first_name} ${financialUser.last_name}`
          : '-',

        assignedByUser: assignment.assigned_by_user
          ? `${assignment.assigned_by_user.first_name} ${assignment.assigned_by_user.last_name}`
          : '-',

        warningMessage: warnings.length > 0 ? warnings.join(' • ') : '-',
        warningType,
      };
    },
    transformFormData: (formData) => {
      return {
        job_id: formData.jobId,
        company_id: formData.companyId || user?.companyId,
        technical_team_id: formData.technicalTeamId || null,
        technical_user_id: formData.technicalUserId || null,
        financial_team_id: formData.financialTeamId || null,
        financial_user_id: formData.financialUserId || null,
        assigned_by: user?.id,
      };
    },
    transformFormDataForEdit: (assignment) => {
      return {
        jobId: assignment.job_id,
        companyId: assignment.company_id,
        technicalTeamId: assignment.technical_team_id,
        technicalUserId: assignment.technical_user_id,
        financialTeamId: assignment.financial_team_id,
        financialUserId: assignment.financial_user_id,
      };
    },
  });

  useEffect(() => {
    const fetchUnassigned = async () => {
      if (!user?.companyId) return;
      setLoadingUnassigned(true);
      try {
        // Restricted user'lar (team_member/team_manager) sadece kendi
        // yarattıkları işleri görsün. ROOT/DIRECTOR/GROUP_MANAGER tüm
        // şirketinkini görür (mevcut davranış).
        const isUnrestricted = UNRESTRICTED_UNASSIGNED_ROLES.has(user?.role);
        const numericUserId = parseInt(user?.id, 10);
        // Defansif: restricted ama user.id Int değilse (race, anonymous),
        // boş liste ile dönüyoruz — Hasura "Int! null" hatasını önlüyor.
        if (!isUnrestricted && !Number.isFinite(numericUserId)) {
          setUnassignedJobs([]);
          return;
        }
        const response = isUnrestricted
          ? await hasuraClient.query(GET_UNASSIGNED_JOBS, {
              companyId: user.companyId,
            })
          : await hasuraClient.query(GET_UNASSIGNED_JOBS_FOR_CREATOR, {
              companyId: user.companyId,
              userId: numericUserId,
            });
        
        // Unassigned jobs'u DynamicCrudPage formatına uygun hale getir
        const transformedJobs = (response?.jobs || []).map(job => {
          const assignment = job.job_assignment || {};
          const technicalUser = assignment.technical_user;
          const financialUser = assignment.financial_user;

          const warnings = [];
          let warningType = 'NONE';

          const today = new Date();
          const techLeft =
            !!technicalUser?.work_end_date &&
            new Date(technicalUser.work_end_date) <= today;

          if (techLeft) {
            warningType = 'TECH_LEFT';
            const name = `${technicalUser.first_name || ''} ${technicalUser.last_name || ''}`.trim();
            warnings.push(
              t('jobAssignments.warnings.technicalUserLeft', {
                name: name || t('common.unknownUser', 'Bilinmeyen kullanıcı'),
              })
            );
          }

          const jobScope = job.consulting_scope || 'MALI_TEKNIK';
          const needsFinancial =
            job.job_status === 'ONAYLANDI' &&
            !financialUser &&
            jobScope !== 'SADECE_TEKNIK' &&
            !job.no_financial_needed;

          if (needsFinancial) {
            warningType =
              warningType === 'NONE' ? 'NO_FINANCIAL' : 'TECH_LEFT_AND_NO_FINANCIAL';
            warnings.push(t('jobAssignments.warnings.missingFinancial'));
          }

          return {
            id: job.id, // İşlem yaparken job id lazım olacak
            job_id: job.id,
            is_unassigned: true, // Atanmamış olduğunu belirten flag
            company_id: user.companyId,
            created_at: job.created_at,
            assignment_id: assignment.id || null, // Varsa kısmi atama ID'si

            jobTitle: job.job_title,
            jobStatus: job.job_status,
            jobType: job.job_type_obj?.name || job.job_type,
            createdByUser: job.user
              ? `${job.user.first_name || ''} ${job.user.last_name || ''}`.trim() || '-'
              : '-',
            projectNumber: job.project_number || '-',
            consultingScope: job.consulting_scope || 'MALI_TEKNIK',
            firmName: job.firm?.firm_name || '-',
        firmShortName: job.firm?.firm_short_name || '-',

            technicalTeamName: assignment.technical_team?.name || '-',
            technicalUserName: technicalUser
              ? `${technicalUser.first_name} ${technicalUser.last_name}`
              : '-',

            financialTeamName: assignment.financial_team?.name || '-',
            financialUserName: financialUser
              ? `${financialUser.first_name} ${financialUser.last_name}`
              : '-',

            assignedByUser: assignment.assigned_by_user
              ? `${assignment.assigned_by_user.first_name} ${assignment.assigned_by_user.last_name}`
              : '-',

            technical_team_id: assignment.technical_team_id || null,
            technical_user_id: assignment.technical_user_id || null,
            financial_team_id: assignment.financial_team_id || null,
            financial_user_id: assignment.financial_user_id || null,
            job,

            warningMessage: warnings.length > 0 ? warnings.join(' • ') : '-',
            warningType,
          };
        });
        
        setUnassignedJobs(transformedJobs);
      } catch (error) {
        console.error('Error fetching unassigned jobs:', error);
      } finally {
        setLoadingUnassigned(false);
      }
    };

    fetchUnassigned();
    // role / id değişirse (login farklı kullanıcı ile yapılırsa) doğru
    // query/variables ile yeniden çekilsin.
  }, [user?.companyId, user?.role, user?.id, dynamicHook.refreshTrigger]);

  // Sadece atama bekleyen işler (GET_UNASSIGNED_JOBS) – tam atanmış işler bu sayfada listelenmez
  const combinedData = [...unassignedJobs]
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.job?.created_at || a.created_at || 0).getTime();
      const dateB = new Date(b.job?.created_at || b.created_at || 0).getTime();
      return dateB - dateA;
    });

  return {
    ...dynamicHook,
    jobAssignments: combinedData, // Üzerine yazarak hem atanmış hem atanmamışları listeledik
    isLoading: dynamicHook.isLoading || loadingUnassigned,
  };
};

export default useJobAssignments;

